import { Component } from '@theme/component';

/**
 * @typedef {Object} SpinTabsRefs
 * @property {HTMLElement} tablist - The tablist container
 * @property {HTMLElement} panels - The panels container
 */

/**
 * @extends {Component<SpinTabsRefs>}
 */
class SpinTabsComponent extends Component {
  #tabs = [];

  connectedCallback() {
    super.connectedCallback();
    this.#hoistButtons();
    this.#bindEvents();

    const requestedIndex = this.#indexFromUrl();
    this.#selectByIndex(requestedIndex ?? 0);
    if (requestedIndex !== null) {
      const scrollTarget = this.closest('.spin-tabs-section') ?? this;
      scrollTarget.scrollIntoView({ block: 'start' });
    }
  }

  /**
   * Reads the `tab` query parameter and returns the index of the tab whose
   * handle (handleized label) matches, or null when absent/unmatched.
   * Enables deep links like /pages/projects?tab=cook
   * @returns {number|null}
   */
  #indexFromUrl() {
    const requested = new URLSearchParams(window.location.search).get('tab');
    if (!requested) return null;

    const index = this.#tabs.findIndex(
      (tab) => tab.dataset.spinTabHandle === requested.toLowerCase()
    );
    return index === -1 ? null : index;
  }

  #hoistButtons() {
    const tablist = this.refs.tablist;
    if (!tablist) return;

    const buttons = this.querySelectorAll('[data-spin-tab-button]');
    for (const button of buttons) {
      tablist.appendChild(button);
    }
    this.#tabs = Array.from(tablist.querySelectorAll('[data-spin-tab-button]'));
  }

  #bindEvents() {
    for (const tab of this.#tabs) {
      tab.addEventListener('click', this.#handleClick);
      tab.addEventListener('keydown', this.#handleKey);
    }
  }

  #handleClick = (event) => {
    const tab = event.currentTarget;
    const index = this.#tabs.indexOf(tab);
    if (index !== -1) this.#selectByIndex(index);
  };

  #handleKey = (event) => {
    if (!this.#tabs.length) return;
    const current = event.currentTarget;
    const index = this.#tabs.indexOf(current);
    if (index === -1) return;

    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#selectByIndex(nextIndex);
    this.#tabs[nextIndex].focus();
  };

  #selectByIndex(activeIndex) {
    const targetId = this.#tabs[activeIndex]?.getAttribute('aria-controls');

    this.#tabs.forEach((tab, i) => {
      const isActive = i === activeIndex;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    for (const panel of this.querySelectorAll('[data-spin-tab-panel]')) {
      const isActive = panel.id === targetId;
      panel.hidden = !isActive;
      if (isActive) this.#refreshPanelMedia(panel);
    }
  }

  #refreshPanelMedia(panel) {
    if (panel.dataset.spinTabActivated === 'true') return;
    panel.dataset.spinTabActivated = 'true';

    for (const iframe of panel.querySelectorAll('iframe')) {
      const src = iframe.getAttribute('src');
      if (src) iframe.setAttribute('src', src);
    }
  }
}

if (!customElements.get('spin-tabs-component')) {
  customElements.define('spin-tabs-component', SpinTabsComponent);
}
